import { useState, useCallback, useEffect } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { InterviewQuestion } from '@recruitai/shared';
import { apiClient } from '../lib/api-client';
import { toast } from 'sonner';

interface UseQuestionEditorResult {
  questions: InterviewQuestion[];
  isLoading: boolean;
  isSaving: boolean;
  updateQuestion: (id: string, updates: Partial<InterviewQuestion>) => void;
  removeQuestion: (id: string) => void;
  addQuestion: (question: Omit<InterviewQuestion, 'id'>) => void;
  saveQuestions: () => Promise<void>;
  copyToClipboard: () => void;
}

export function useQuestionEditor(
  jobId: string,
  candidateId: string,
  initialQuestions?: InterviewQuestion[]
): UseQuestionEditorResult {
  const [questions, setQuestions] = useState<InterviewQuestion[]>(initialQuestions || []);
  const [isLoading, setIsLoading] = useState(!initialQuestions);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (initialQuestions) {
      setQuestions(initialQuestions);
      setIsLoading(false);
    }
  }, [initialQuestions]);

  const updateQuestion = useCallback((id: string, updates: Partial<InterviewQuestion>) => {
    setQuestions((prev) =>
      prev.map((q) => (q.id === id ? { ...q, ...updates } : q))
    );
  }, []);

  const removeQuestion = useCallback((id: string) => {
    setQuestions((prev) => prev.filter((q) => q.id !== id));
  }, []);

  const addQuestion = useCallback((question: Omit<InterviewQuestion, 'id'>) => {
    setQuestions((prev) => [...prev, { ...question, id: uuidv4() }]);
  }, []);

  const saveQuestions = useCallback(async () => {
    try {
      setIsSaving(true);
      await apiClient.put(
        `/jobs/${jobId}/candidates/${candidateId}/interview-questions`,
        { questions }
      );
      toast.success('Perguntas salvas com sucesso');
    } catch (err) {
      toast.error('Erro ao salvar perguntas');
      throw err;
    } finally {
      setIsSaving(false);
    }
  }, [jobId, candidateId, questions]);

  const copyToClipboard = useCallback(() => {
    const text = questions
      .map((q, i) => `${i + 1}. [${q.category}] ${q.question}\n   Racional: ${q.rationale}\n   O que avaliar: ${q.whatToLookFor}`)
      .join('\n\n');
    navigator.clipboard.writeText(text);
    toast.success('Roteiro copiado para a área de transferência');
  }, [questions]);

  return {
    questions,
    isLoading,
    isSaving,
    updateQuestion,
    removeQuestion,
    addQuestion,
    saveQuestions,
    copyToClipboard,
  };
}
